import { useEffect, useRef } from 'react'
import apiClient from '../services/apiClient'
import { getOrCreateDeviceId } from '../utils/deviceId'

const HEARTBEAT_MS = 45_000
const MAX_RECOVERY_ATTEMPTS = 2

export interface PlaybackLeaseResource {
  type: 'recording' | 'video'
  id: number
  /** Lease returned by `/playback/token`; scopes heartbeat/release to that exact lease. */
  leaseToken?: string
}

/**
 * While `active`, renews playback lease and releases it on unmount.
 * Passing `resource` scopes heartbeat/release to the media being played, so switching
 * between videos cannot release the lease that the next player just acquired.
 *
 * Callers must only turn `active` on once the lease for `resource` is acquired
 * (i.e. after `/playback/token` resolved), otherwise the first heartbeat can race
 * ahead of the lease and get rejected.
 */
export function usePlaybackLease(active: boolean, resource?: PlaybackLeaseResource) {
  const deviceIdRef = useRef(getOrCreateDeviceId())
  const resourceRef = useRef(resource)
  resourceRef.current = resource

  const resourceKey = resource ? `${resource.type}:${resource.id}:${resource.leaseToken || ''}` : ''

  useEffect(() => {
    if (!active) return

    const deviceId = deviceIdRef.current
    const leaseResource = resourceRef.current
    let leaseToken = leaseResource?.leaseToken
    let cancelled = false
    let recoveryAttempts = 0

    const buildPayload = () => ({
      deviceId,
      ...(leaseResource ? { resourceType: leaseResource.type, resourceId: leaseResource.id } : {}),
      ...(leaseToken ? { leaseToken } : {}),
    })

    const reacquireLease = async () => {
      if (!leaseResource) return false
      try {
        const response = await apiClient.get('/student/playback/token', {
          params: { type: leaseResource.type, id: leaseResource.id, deviceId },
        })
        const nextLeaseToken = response.data?.data?.leaseToken
        if (typeof nextLeaseToken === 'string' && nextLeaseToken) {
          leaseToken = nextLeaseToken
        }
        return true
      } catch {
        return false
      }
    }

    const beat = async () => {
      try {
        await apiClient.post('/student/playback/heartbeat', buildPayload())
        recoveryAttempts = 0
      } catch (error) {
        if (cancelled) return

        const ax = error as { response?: { status?: number; data?: { code?: string } } }
        const lostLease = ax.response?.status === 409 && ax.response?.data?.code === 'NO_LEASE'

        if (lostLease && recoveryAttempts < MAX_RECOVERY_ATTEMPTS) {
          recoveryAttempts += 1
          if (await reacquireLease()) return
        }

        console.error('Playback heartbeat failed', error)
      }
    }

    // No immediate beat: the lease was just acquired and is valid for longer than one interval.
    const timer = window.setInterval(() => {
      void beat()
    }, HEARTBEAT_MS)

    return () => {
      cancelled = true
      window.clearInterval(timer)
      void apiClient.post('/student/playback/release', buildPayload()).catch(() => {
        /* ignore */
      })
    }
  }, [active, resourceKey])

  return deviceIdRef.current
}
